import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../StuStyles/StuJobApplicationForm.css";
import { MdClose } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ApiService from "../../../nhrc_apis/ApiService";

const { JobAPI } = ApiService;

const StuJobApplicationForm = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const selectedJob = location.state?.job || null;

  const [loading,        setLoading]        = useState(true);
  const [submitLoading,  setSubmitLoading]  = useState(false);
  const [experienceType, setExperienceType] = useState("");
  const [errors,         setErrors]         = useState({});

  const [experienceList, setExperienceList] = useState([
    { company: "", role: "", joining: "", relieving: "" },
  ]);

  const [formData, setFormData] = useState({
    firstName:       "",
    lastName:        "",
    phone:           "",
    email:           "",
    dob:             "",
    gender:          "",
    location:        "",
    panNumber:       "",
    pan:             null,
    resume:          null,
    photo:           null,
    linkedIn:        "",
    qualification:   "",
    specialization:  "",
    university:      "",
    college:         "",
    yearOfPassing:   "",
    positionApplied: selectedJob?.title || "",
    preferredMode:   "",
    keySkills:       "",
    expectedSalary:  "",
    whyHireMe:       "",
  });

  // ─── LOAD PROFILE ─────────────────────────────────────────
  useEffect(() => {
    if (!selectedJob) {
      toast.error("No Job Selected");
      setTimeout(() => navigate("/Student/JobStrategy"), 1500);
      return;
    }
    fetchStudentProfile();
  }, []);

  // ─── FETCH PROFILE ────────────────────────────────────────
  const fetchStudentProfile = async () => {
    try {
      setLoading(true);

      const storedMember  = localStorage.getItem("member");
      const loggedInUser  = localStorage.getItem("loggedInUser");

      if (!storedMember && !loggedInUser) {
        toast.error("Student data not found");
        navigate("/student-login");
        return;
      }

      const parsedMember = storedMember
        ? JSON.parse(storedMember)
        : JSON.parse(loggedInUser);

      console.log("PARSED MEMBER :", parsedMember);

      const fullName  = parsedMember?.full_name || parsedMember?.name || "";
      const splitName = fullName.split(" ");
      const firstName = splitName[0] || "";
      const lastName  = splitName.slice(1).join(" ");

      setFormData((prev) => ({
        ...prev,
        firstName,
        lastName,
        phone:          parsedMember?.mobile           || parsedMember?.phone           || "",
        email:          parsedMember?.email            || "",
        dob:            parsedMember?.dob              || parsedMember?.date_of_birth   || "",
        gender:         parsedMember?.gender           || "",
        location:       parsedMember?.district         || parsedMember?.location        || "",
        qualification:  parsedMember?.qualification    || parsedMember?.highest_qualification || "",
        specialization: parsedMember?.department       || parsedMember?.specialization  || "",
        university:     parsedMember?.university_name  || parsedMember?.university      || "",
        college:        parsedMember?.college_name     || parsedMember?.college         || "",
        yearOfPassing:  parsedMember?.end_year         || parsedMember?.year_of_passing || "",
        positionApplied: selectedJob?.title            || "",
      }));

    } catch (error) {
      console.log("PROFILE ERROR :", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  // ─── VALIDATE SINGLE FIELD ────────────────────────────────
  const validateField = (name, value) => {
    let error = "";

    switch (name) {

      case "firstName":
        if (!value || !value.trim()) {
          error = "First name is required.";
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "First name must contain only letters.";
        } else if (value.trim().length < 2) {
          error = "First name must be at least 2 characters.";
        }
        break;

      case "lastName":
        if (value && !/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "Last name must contain only letters.";
        }
        break;

      case "phone":
        if (!value || !value.trim()) {
          error = "Phone number is required.";
        } else if (!/^[0-9]{10}$/.test(value.trim())) {
          error = "Phone number must be exactly 10 digits.";
        }
        break;

      case "email":
        if (!value || !value.trim()) {
          error = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = "Enter a valid email address (e.g. name@example.com).";
        }
        break;

      case "dob":
        if (!value) {
          error = "Date of birth is required.";
        } else {
          const today   = new Date();
          const dobDate = new Date(value);
          const age     = today.getFullYear() - dobDate.getFullYear();
          if (age < 16 || age > 60) {
            error = "Age must be between 16 and 60 years.";
          }
        }
        break;

      case "gender":
        if (!value) {
          error = "Please select a gender.";
        }
        break;

      case "location":
        if (!value || !value.trim()) {
          error = "Location is required.";
        } else if (value.trim().length < 2) {
          error = "Location must be at least 2 characters.";
        }
        break;

      case "panNumber":
        if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.trim().toUpperCase())) {
          error = "Enter a valid PAN number (e.g. ABCDE1234F).";
        }
        break;

      case "resume":
        if (!value) {
          error = "Resume is required.";
        }
        break;

      case "linkedIn":
        if (value && !/^https?:\/\/(www\.)?linkedin\.com\/.*$/.test(value.trim())) {
          error = "Enter a valid LinkedIn URL.";
        }
        break;

      case "qualification":
        if (!value || !value.trim()) {
          error = "Qualification is required.";
        }
        break;

      case "specialization":
        if (!value || !value.trim()) {
          error = "Specialization is required.";
        }
        break;

      case "university":
        if (!value || !value.trim()) {
          error = "University is required.";
        }
        break;

      case "college":
        if (!value || !value.trim()) {
          error = "College is required.";
        }
        break;

      case "yearOfPassing":
        if (!value || !value.trim()) {
          error = "Year of passing is required.";
        } else if (!/^[0-9]{4}$/.test(value.trim())) {
          error = "Enter a valid 4-digit year (e.g. 2024).";
        } else if (
          parseInt(value.trim()) < 1980 ||
          parseInt(value.trim()) > 2035
        ) {
          error = "Year must be between 1980 and 2035.";
        }
        break;

      case "preferredMode":
        if (!value) {
          error = "Please select a preferred work mode.";
        }
        break;

      case "keySkills":
        if (!value || !value.trim()) {
          error = "Key skills are required.";
        } else if (value.trim().length < 3) {
          error = "Please enter at least 3 characters for key skills.";
        }
        break;

      case "expectedSalary":
        if (!value || !value.trim()) {
          error = "Expected salary is required.";
        } else if (!/^[0-9]+$/.test(value.trim())) {
          error = "Expected salary must be a number.";
        }
        break;

      case "whyHireMe":
        if (!value || !value.trim()) {
          error = "Please tell us why we should hire you.";
        } else if (value.trim().length < 20) {
          error = "Please write at least 20 characters.";
        }
        break;

      case "experienceType":
        if (!value) {
          error = "Please select experience level.";
        }
        break;

      default:
        break;
    }

    return error;
  };

  // ─── VALIDATE ALL FIELDS ──────────────────────────────────
  const validateAll = () => {
    const fields = [
      "firstName", "lastName", "phone", "email", "dob", "gender",
      "location", "panNumber", "resume", "linkedIn",
      "qualification", "specialization", "university", "college",
      "yearOfPassing", "preferredMode", "keySkills", "expectedSalary",
      "whyHireMe", "experienceType",
    ];

    const newErrors = {};

    fields.forEach((field) => {
      const value = field === "experienceType" ? experienceType : formData[field];
      const error = validateField(field, value);
      if (error) newErrors[field] = error;
    });

    // Validate experience list if EXPERIENCED
    if (experienceType === "EXPERIENCED") {
      experienceList.forEach((exp, index) => {
        if (!exp.company.trim()) newErrors[`exp_company_${index}`] = "Company name is required.";
        if (!exp.role.trim())    newErrors[`exp_role_${index}`]    = "Role is required.";
        if (!exp.joining)        newErrors[`exp_joining_${index}`] = "Joining date is required.";
        if (!exp.relieving)      newErrors[`exp_relieving_${index}`] = "Relieving date is required.";
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── HANDLE INPUT CHANGE ──────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (files?.length) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      const error = validateField(name, files[0]);
      setErrors((prev) => ({ ...prev, [name]: error }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // ─── EXPERIENCE CHANGE ────────────────────────────────────
  const handleExperienceChange = (index, field, value) => {
    const updated = [...experienceList];
    updated[index][field] = value;
    setExperienceList(updated);

    // Clear experience error on change
    const errorKey = `exp_${field}_${index}`;
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [errorKey]: "" }));
    }
  };

  // ─── ADD EXPERIENCE ───────────────────────────────────────
  const addExperience = () => {
    setExperienceList([
      ...experienceList,
      { company: "", role: "", joining: "", relieving: "" },
    ]);
  };

  // ─── DELETE EXPERIENCE ────────────────────────────────────
  const deleteExperience = (indexToDelete) => {
    if (experienceList.length === 1) {
      toast.warning("At least one experience is required");
      return;
    }
    setExperienceList(experienceList.filter((_, index) => index !== indexToDelete));
  };

  // ─── FILE TO BASE64 ───────────────────────────────────────
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) { resolve(""); return; }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload  = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // ─── HANDLE SUBMIT ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = validateAll();

    if (!isValid) {
      toast.error("Please fix all errors before submitting.", { autoClose: 2500 });
      // Scroll to first error
      const firstErrorEl = document.querySelector(".field-error-text");
      if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    try {
      setSubmitLoading(true);

      const studentToken = localStorage.getItem("studentToken");
      console.log("STUDENT TOKEN :", studentToken);

      if (!studentToken) {
        toast.error("Student Login Required");
        navigate("/student-login");
        return;
      }

      localStorage.setItem("token", studentToken);

      let formattedExperiences = [];
      if (experienceType.toUpperCase() === "EXPERIENCED") {
        formattedExperiences = experienceList.map((exp) => ({
          previous_company: exp.company,
          previous_role:    exp.role,
          date_of_joining:  exp.joining,
          relieving_date:   exp.relieving,
        }));
      }

      const resumeBase64 = await convertFileToBase64(formData.resume);
      const panBase64    = await convertFileToBase64(formData.pan);
      const photoBase64  = await convertFileToBase64(formData.photo);

      const payload = {
        first_name:            formData.firstName,
        last_name:             formData.lastName,
        phone_number:          formData.phone,
        email:                 formData.email,
        date_of_birth:         formData.dob,
        gender:                formData.gender,
        location:              formData.location,
        pan_number:            formData.panNumber,
        pan_card_file:         panBase64    || "",
        resume_file:           resumeBase64 || "",
        photo_file:            photoBase64  || "",
        linkedin_url:          formData.linkedIn,
        highest_qualification: formData.qualification,
        specialization:        formData.specialization,
        university:            formData.university,
        college:               formData.college,
        year_of_passing:       formData.yearOfPassing,
        position_applied_for:  formData.positionApplied,
        preferred_work_mode:   formData.preferredMode,
        key_skills:            formData.keySkills,
        expected_salary:       formData.expectedSalary,
        why_hire_me:           formData.whyHireMe,
        experience_type:       experienceType || "FRESHER",
        experiences:           formattedExperiences,
      };

      console.log("FINAL APPLY PAYLOAD :", payload);
      console.log("JOB ID :", selectedJob?.job_id || selectedJob?.id);

      const response = await JobAPI.applyJob(
        selectedJob?.job_id || selectedJob?.id,
        payload
      );

      console.log("APPLY RESPONSE :", response);
      toast.success("Job Applied Successfully");
      setTimeout(() => navigate("/Student/JobStrategy"), 2000);

    } catch (error) {
      console.log("APPLY ERROR :",          error);
      console.log("APPLY ERROR RESPONSE :", error?.response?.data);
      console.log("APPLY ERROR STATUS :",   error?.response?.status);

      if (error?.response?.status === 401) {
        toast.error("Unauthorized. Please login again.");
        navigate("/student-login");
      } else if (error?.response?.status === 403) {
        toast.error("Access Forbidden");
      } else if (error?.response?.status === 422) {
        console.log("VALIDATION ERROR :", error?.response?.data);
        toast.error("Validation Error. Please check all fields.");
      } else {
        toast.error(
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed To Apply Job"
        );
      }

    } finally {
      setSubmitLoading(false);
    }
  };

  // ─── LOADING ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <h4>Loading Profile...</h4>
      </div>
    );
  }

  // ─── RENDER ───────────────────────────────────────────────
  return (
    <div className="jobapp-container">
      <ToastContainer position="top-center" theme="colored" />

      <h2 className="jobapp-heading">Job Application Form</h2>

      <div className="header-app">
        <div className="selected-position">{formData.positionApplied}</div>
      </div>

      <form className="jobapp-form" onSubmit={handleSubmit} noValidate>

        {/* ── PERSONAL INFO ─────────────────────────────── */}
        <section className="jobapp-section">
          <h3 className="jobapp-section-title">Personal & Contact Information</h3>
          <div className="jobapp-grid">

            <label>First Name
              <input
                type="text" name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`jobapp-input ${errors.firstName ? "input-error" : ""}`}
              />
              {errors.firstName && <span className="field-error-text">{errors.firstName}</span>}
            </label>

            <label>Last Name
              <input
                type="text" name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`jobapp-input ${errors.lastName ? "input-error" : ""}`}
              />
              {errors.lastName && <span className="field-error-text">{errors.lastName}</span>}
            </label>

            <label>Phone Number
              <input
                type="text" name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                maxLength={10}
                placeholder="Enter 10-digit phone number"
                className={`jobapp-input ${errors.phone ? "input-error" : ""}`}
              />
              {errors.phone && <span className="field-error-text">{errors.phone}</span>}
            </label>

            <label>Email
              <input
                type="email" name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className={`jobapp-input ${errors.email ? "input-error" : ""}`}
              />
              {errors.email && <span className="field-error-text">{errors.email}</span>}
            </label>

            <label>Date Of Birth
              <input
                type="date" name="dob"
                value={formData.dob}
                onChange={handleInputChange}
                className={`jobapp-input ${errors.dob ? "input-error" : ""}`}
              />
              {errors.dob && <span className="field-error-text">{errors.dob}</span>}
            </label>

            <label>Gender
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className={`jobapp-input ${errors.gender ? "input-error" : ""}`}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.gender && <span className="field-error-text">{errors.gender}</span>}
            </label>

            <label>Location
              <input
                type="text" name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Enter your location"
                className={`jobapp-input ${errors.location ? "input-error" : ""}`}
              />
              {errors.location && <span className="field-error-text">{errors.location}</span>}
            </label>

            <label>PAN Number
              <input
                type="text" name="panNumber"
                value={formData.panNumber}
                onChange={handleInputChange}
                placeholder="e.g. ABCDE1234F"
                maxLength={10}
                className={`jobapp-input ${errors.panNumber ? "input-error" : ""}`}
              />
              {errors.panNumber && <span className="field-error-text">{errors.panNumber}</span>}
            </label>

            <label>Upload PAN Card
              <input
                type="file" name="pan"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleInputChange}
                className="jobapp-file"
              />
            </label>

            <label>Upload Resume
              <input
                type="file" name="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleInputChange}
                className={`jobapp-file ${errors.resume ? "input-error" : ""}`}
              />
              {errors.resume && <span className="field-error-text">{errors.resume}</span>}
            </label>

            <label>Upload Photo
              <input
                type="file" name="photo"
                accept=".jpg,.jpeg,.png"
                onChange={handleInputChange}
                className="jobapp-file"
              />
            </label>

            <label>LinkedIn URL
              <input
                type="text" name="linkedIn"
                value={formData.linkedIn}
                onChange={handleInputChange}
                placeholder="https://linkedin.com/in/yourprofile"
                className={`jobapp-input ${errors.linkedIn ? "input-error" : ""}`}
              />
              {errors.linkedIn && <span className="field-error-text">{errors.linkedIn}</span>}
            </label>

          </div>
        </section>

        {/* ── EDUCATION ─────────────────────────────────── */}
        <section className="jobapp-section">
          <h3 className="jobapp-section-title">Educational Information</h3>
          <div className="jobapp-grid">

            <label>Qualification
              <input
                type="text" name="qualification"
                value={formData.qualification}
                onChange={handleInputChange}
                placeholder="e.g. B.Tech, MBA"
                className={`jobapp-input ${errors.qualification ? "input-error" : ""}`}
              />
              {errors.qualification && <span className="field-error-text">{errors.qualification}</span>}
            </label>

            <label>Specialization
              <input
                type="text" name="specialization"
                value={formData.specialization}
                onChange={handleInputChange}
                placeholder="e.g. Computer Science"
                className={`jobapp-input ${errors.specialization ? "input-error" : ""}`}
              />
              {errors.specialization && <span className="field-error-text">{errors.specialization}</span>}
            </label>

            <label>University
              <input
                type="text" name="university"
                value={formData.university}
                onChange={handleInputChange}
                placeholder="Enter university name"
                className={`jobapp-input ${errors.university ? "input-error" : ""}`}
              />
              {errors.university && <span className="field-error-text">{errors.university}</span>}
            </label>

            <label>College
              <input
                type="text" name="college"
                value={formData.college}
                onChange={handleInputChange}
                placeholder="Enter college name"
                className={`jobapp-input ${errors.college ? "input-error" : ""}`}
              />
              {errors.college && <span className="field-error-text">{errors.college}</span>}
            </label>

            <label>Year Of Passing
              <input
                type="text" name="yearOfPassing"
                value={formData.yearOfPassing}
                onChange={handleInputChange}
                placeholder="e.g. 2024"
                maxLength={4}
                className={`jobapp-input ${errors.yearOfPassing ? "input-error" : ""}`}
              />
              {errors.yearOfPassing && <span className="field-error-text">{errors.yearOfPassing}</span>}
            </label>

          </div>
        </section>

        {/* ── JOB DETAILS ───────────────────────────────── */}
        <section className="jobapp-section">
          <h3 className="jobapp-section-title">Job Details</h3>
          <div className="jobapp-grid">

            <label>Position Applied
              <input
                type="text" name="positionApplied"
                value={formData.positionApplied}
                readOnly
                className="jobapp-input"
              />
            </label>

            <label>Preferred Mode
              <select
                name="preferredMode"
                value={formData.preferredMode}
                onChange={handleInputChange}
                className={`jobapp-input ${errors.preferredMode ? "input-error" : ""}`}
              >
                <option value="">Select Mode</option>
                <option value="Remote">Remote</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Onsite">Onsite</option>
              </select>
              {errors.preferredMode && <span className="field-error-text">{errors.preferredMode}</span>}
            </label>

            <label>Key Skills
              <input
                type="text" name="keySkills"
                value={formData.keySkills}
                onChange={handleInputChange}
                placeholder="e.g. React, Python, SQL"
                className={`jobapp-input ${errors.keySkills ? "input-error" : ""}`}
              />
              {errors.keySkills && <span className="field-error-text">{errors.keySkills}</span>}
            </label>

            <label>Expected Salary
              <input
                type="text" name="expectedSalary"
                value={formData.expectedSalary}
                onChange={handleInputChange}
                placeholder="Enter expected salary in numbers"
                className={`jobapp-input ${errors.expectedSalary ? "input-error" : ""}`}
              />
              {errors.expectedSalary && <span className="field-error-text">{errors.expectedSalary}</span>}
            </label>

          </div>

          <label>Why Hire Me ?
            <textarea
              name="whyHireMe"
              value={formData.whyHireMe}
              onChange={handleInputChange}
              placeholder="Write at least 20 characters..."
              className={`jobapp-textarea fullwidth ${errors.whyHireMe ? "input-error" : ""}`}
              style={{ width: "100%" }}
            />
            {errors.whyHireMe && <span className="field-error-text">{errors.whyHireMe}</span>}
          </label>
        </section>

        {/* ── EXPERIENCE ────────────────────────────────── */}
        <section className="jobapp-section">
          <h3 className="jobapp-section-title">Professional Experience</h3>

          <label>Experience Level
            <select
              className={`jobapp-input ${errors.experienceType ? "input-error" : ""}`}
              value={experienceType}
              onChange={(e) => {
                setExperienceType(e.target.value);
                const error = validateField("experienceType", e.target.value);
                setErrors((prev) => ({ ...prev, experienceType: error }));
              }}
            >
              <option value="">Select Experience</option>
              <option value="FRESHER">Fresher</option>
              <option value="EXPERIENCED">Experienced</option>
            </select>
            {errors.experienceType && <span className="field-error-text">{errors.experienceType}</span>}
          </label>

          {experienceType === "EXPERIENCED" && (
            <div className="jobapp-exp-wrapper">
              <button
                type="button"
                className="jobapp-add-btn mb-2"
                onClick={addExperience}
              >
                + Add Experience
              </button>

              {experienceList.map((exp, index) => (
                <div className="jobapp-exp-card" key={index}>
                  <div className="d-flex justify-content-between">
                    <h4 className="jobapp-exp-title">Experience {index + 1}</h4>
                    <button
                      type="button"
                      className="trash-btn"
                      onClick={() => deleteExperience(index)}
                    >
                      <MdClose />
                    </button>
                  </div>

                  <div className="jobapp-exp-grid">

                    <label>Company
                      <input
                        type="text"
                        value={exp.company}
                        onChange={(e) => handleExperienceChange(index, "company", e.target.value)}
                        placeholder="Enter company name"
                        className={`jobapp-input ${errors[`exp_company_${index}`] ? "input-error" : ""}`}
                      />
                      {errors[`exp_company_${index}`] && <span className="field-error-text">{errors[`exp_company_${index}`]}</span>}
                    </label>

                    <label>Role
                      <input
                        type="text"
                        value={exp.role}
                        onChange={(e) => handleExperienceChange(index, "role", e.target.value)}
                        placeholder="Enter your role"
                        className={`jobapp-input ${errors[`exp_role_${index}`] ? "input-error" : ""}`}
                      />
                      {errors[`exp_role_${index}`] && <span className="field-error-text">{errors[`exp_role_${index}`]}</span>}
                    </label>

                    <label>Joining Date
                      <input
                        type="date"
                        value={exp.joining}
                        onChange={(e) => handleExperienceChange(index, "joining", e.target.value)}
                        className={`jobapp-input ${errors[`exp_joining_${index}`] ? "input-error" : ""}`}
                      />
                      {errors[`exp_joining_${index}`] && <span className="field-error-text">{errors[`exp_joining_${index}`]}</span>}
                    </label>

                    <label>Relieving Date
                      <input
                        type="date"
                        value={exp.relieving}
                        onChange={(e) => handleExperienceChange(index, "relieving", e.target.value)}
                        className={`jobapp-input ${errors[`exp_relieving_${index}`] ? "input-error" : ""}`}
                      />
                      {errors[`exp_relieving_${index}`] && <span className="field-error-text">{errors[`exp_relieving_${index}`]}</span>}
                    </label>

                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── SUBMIT ────────────────────────────────────── */}
        <section className="jobapp-section jobapp-captcha-section">
          <button
            type="submit"
            className="jobapp-submit-btn mb-2"
            disabled={submitLoading}
          >
            {submitLoading ? "Submitting..." : "Submit Application"}
          </button>
        </section>

      </form>

      <ToastContainer />
    </div>
  );
};

export default StuJobApplicationForm;